// R6 — Turn timer indicator for the tavern phase.
//
// Renders a small circular ring + countdown number alongside the
// other stat chips in the tavern top bar. The store owns the actual
// countdown (`turnTimerMs`, decremented via `tick(deltaMs)`) — this
// component is purely presentational.
//
// Color shifts as time pressure builds:
//   > 30s  → bone   (calm)
//   10-30s → marker (warm warning)
//   < 10s  → aerosolRed, pulsing (urgent)
//
// When `active` is false (e.g., discover modal open, combat phase),
// the ring fades and the pulse halts.

import { motion } from 'framer-motion';

interface TurnTimerProps {
  /** Milliseconds remaining on the current tavern turn. */
  remainingMs: number;
  /** Full duration of a turn (used to compute the ring fill). */
  totalMs: number;
  /** When false, the ring desaturates and the pulse stops. */
  active: boolean;
  /** Rendered diameter in px. Portrait passes 44 so the timer matches the
      tap-target floor of the header chrome around it. */
  size?: number;
}

const DEFAULT_SIZE = 36;
const STROKE = 3;

function colorForRemaining(remainingMs: number, active: boolean): {
  ring: string;
  text: string;
} {
  if (!active) return { ring: '#8a7e6c', text: '#8a7e6c' };
  if (remainingMs <= 10_000) return { ring: '#e6342a', text: '#e6342a' };
  if (remainingMs <= 30_000) return { ring: '#c8b89a', text: '#c8b89a' };
  return { ring: '#f5ecd6', text: '#f5ecd6' };
}

export function TurnTimer({
  remainingMs,
  totalMs,
  active,
  size: SIZE = DEFAULT_SIZE,
}: TurnTimerProps) {
  const RADIUS = (SIZE - STROKE) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const clampedMs = Math.max(0, remainingMs);
  const fraction = totalMs > 0 ? clampedMs / totalMs : 0;
  const dashOffset = CIRCUMFERENCE * (1 - fraction);
  const seconds = Math.ceil(clampedMs / 1000);
  const { ring, text } = colorForRemaining(clampedMs, active);
  const urgent = active && clampedMs > 0 && clampedMs <= 10_000;

  return (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ width: SIZE, height: SIZE }}
      aria-label={
        active
          ? `turn timer ${seconds} seconds remaining`
          : `turn timer paused at ${seconds} seconds`
      }
      role="timer"
    >
      <motion.svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        animate={
          urgent
            ? { opacity: [1, 0.55, 1] }
            : { opacity: active ? 1 : 0.45 }
        }
        transition={
          urgent
            ? { duration: 0.85, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.25 }
        }
        aria-hidden
      >
        {/* Track */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="rgba(245,236,214,0.12)"
          strokeWidth={STROKE}
        />
        {/* Progress */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={ring}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          style={{ transition: 'stroke-dashoffset 120ms linear, stroke 200ms linear' }}
        />
      </motion.svg>
      <span
        className="absolute inset-0 flex items-center justify-center font-data text-[11px] font-bold tabular-nums md:text-[12px]"
        style={{ color: text }}
      >
        {active ? seconds : '⏸'}
      </span>
    </span>
  );
}
